import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getAgentAccountOverview } from "../services/agentAccountApi";

const AgentAccountOverviewContext = createContext(null);

export function AgentAccountOverviewProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Exemption is already loaded by AgentBillingStatusProvider — skip the
      // heavy sales scan so account-overview can finish inside the FE timeout.
      const payload = await getAgentAccountOverview({ skipBillingExemption: true });
      setData(payload || null);
      return payload;
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "טעינה נכשלה");
      return null;
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      loadedOnce,
      refresh,
    }),
    [data, loading, error, loadedOnce, refresh]
  );

  return (
    <AgentAccountOverviewContext.Provider value={value}>
      {children}
    </AgentAccountOverviewContext.Provider>
  );
}

export function useAgentAccountOverview() {
  const ctx = useContext(AgentAccountOverviewContext);
  if (!ctx) {
    throw new Error("useAgentAccountOverview requires AgentAccountOverviewProvider");
  }
  return ctx;
}

export function useAgentAccountOverviewOptional() {
  return useContext(AgentAccountOverviewContext);
}

/**
 * Merge agency display SSOT from account overview, entitlements, billing.
 */
export function pickAgencyDisplayStatus({
  accountOverview,
  entitlementsPayload,
  billingStatus,
} = {}) {
  return (
    accountOverview?.agencyDisplayStatus ??
    accountOverview?.agent?.agencyDisplayStatus ??
    entitlementsPayload?.agencyDisplayStatus ??
    billingStatus?.agencyDisplayStatus ??
    null
  );
}
